import {
  GRADEBOOK_SETTINGS,
  formatDisplayGrade,
  formatDisplayPoints,
  getToolIds,
  round,
  shouldHideSidePanel,
} from '@macmillan-learning/gradebook-shared';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Tooltip from '@mui/material/Tooltip';
import classNames from 'classnames';
import { isEqual } from 'lodash';
import { camelCase, kebabCase } from 'lodash/fp';
import dayjs from '../../../../../utils/dayjs';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';

import { updateGradebook } from '../../../../../actions/updateGradebook';
import {
  createIndividualStudentGradebookUrl,
  formatDisplayPercentage,
  getAssignedPoints,
} from '../../../../../utils/InstructorGradebookHelpers';
import { getConfigObject } from '../../../../../utils/config';
import { ISG_SORT_ORDER_OPTIONS } from '../../../../../utils/constants';
import {
  getAssignmentRowData,
  getStudentRowData,
} from '../../../../InstructorGradebookContainer/Components/GridCell/GridCellDataHelpers';
import GridCellBorder from '../../../../InstructorGradebookContainer/Components/GridCellBorder/GridCellBorder';
import MLIcon from '../../../../MLIcon';
import MLTooltip from '../../../../MLTooltip';
import useSidePanel from '../../../../SidePanel/hooks/useSidePanel';
import {
  CLOSE_SIDE_PANEL,
  SET_PANEL_PARAMS,
} from '../../../../SidePanel/store/actions';
import UngradedIcon from '../../../../UngradedIcon/UngradedIcon';
import {
  getDueDateException,
  getFormattedTime,
  getGradingType,
  getRoundPercentTypeValue,
  getStudentAssignmentExceptionDate,
  hasTimeAccommodationException,
  roundToTwoDecimals,
} from '../../utils';
import s from './TableCells.module.css';

const MOBILE_VIEW = {
  DUE_DATE: 'DUE_DATE',
  COMPLETED: 'COMPLETED',
};

export const AssignmentLabelCell = ({
  assignment,
  user,
  getLaunchUrl,
  handleLocalLaunch,
  isDropdownOpen,
}) => {
  const assignmentData = getAssignmentRowData({
    cellData: assignment,
    user,
    columnIndex: 0,
    leftGridColumnCount: 0,
    getLaunchUrl,
    handleLocalLaunch,
    truncate: false,
    showDueDate: false,
    isIndividualStudentGradebook: true,
    isDropdownOpen,
  });

  return (
    <div
      className={s.assignmentLabel}
      data-test-id="isg-assignment-label-cell"
      role="cell"
    >
      {assignmentData?.content}
    </div>
  );
};

export const MobileAssignmentLabelCell = ({
  assignment,
  user,
  getLaunchUrl,
  handleLocalLaunch,
}) => {
  const { isDropped, dateCompleted } = assignment;

  return (
    <div className={s.assignmentInfo}>
      <AssignmentLabelCell
        assignment={assignment}
        user={user}
        getLaunchUrl={getLaunchUrl}
        handleLocalLaunch={handleLocalLaunch}
        isMobile
      />
      <DueDateCell assignment={assignment} isMobile />
      <DateCell date={dateCompleted} mobileView={MOBILE_VIEW.COMPLETED} />
      <TimeSpentCell assignment={assignment} isMobile />
      {isDropped && <div className={s.dropLowest}>Dropped</div>}
    </div>
  );
};

export const ScoreCell = ({
  studentFullName,
  assignment,
  settings,
  user,
  isMobile,
  getLaunchUrl,
  getStudentReportLink,
  hasStudentReport,
  isTotal,
  showPercent,
  isgCurrentView,
  isChat = false,
  categoryName = '',
}) => {
  const dispatch = useDispatch();
  const { state: sidePanelState, dispatch: sidePanelDispatch } = useSidePanel();
  const { FF_SIDE_PANEL_ACTIVE, FF_HIDE_GUIDED_READING_RESPONSES } =
    getConfigObject();
  const shouldSidePanelBeActive =
    FF_SIDE_PANEL_ACTIVE && !shouldHideSidePanel(assignment.tool) && !isChat;
  const shouldCloseSidePanel =
    FF_SIDE_PANEL_ACTIVE && shouldHideSidePanel(assignment.tool);
  const roundPercentType = getRoundPercentTypeValue(settings);

  const scoreData = getStudentRowData({
    cellData: assignment,
    showPercent,
    roundPercentType,
    user,
    isMobileTotal: isMobile && isTotal,
    isgCurrentView: isgCurrentView,
  });

  const { isUngraded, isAggregateCell, receivedLatePenalty, isOverdue } =
    assignment;

  const isCellSelected =
    shouldSidePanelBeActive &&
    assignment.id &&
    assignment.id === sidePanelState.resourceId;

  const showGradeOverride = (_e) => {
    const assignmentException = getStudentAssignmentExceptionDate(assignment);
    const dueDate = assignmentException || assignment.assignment_due_at;

    let studentAssignmentLink;
    const tool = assignment.tool;
    const hasLaunched = assignment.hasLaunched;
    const supportsUserAssignmentLinks =
      tool === getToolIds().assessment || tool === getToolIds().goreact;
    if (supportsUserAssignmentLinks && hasLaunched) {
      const activityLink = getLaunchUrl({
        ...assignment,
        actual_course_id: assignment.course_id,
      });
      studentAssignmentLink = `${activityLink}&studentUserId=${assignment.studentId}`;
    }

    if (shouldSidePanelBeActive) {
      const isgUrl = createIndividualStudentGradebookUrl({
        studentId: assignment.studentId,
        showPercent,
      });

      const isAssessment = tool === getToolIds().assessment;

      const shouldHideResponses =
        FF_HIDE_GUIDED_READING_RESPONSES && assignment.subtype === 'cca';

      sidePanelDispatch({
        type: SET_PANEL_PARAMS,
        payload: {
          resourceId: assignment.id,
          studentId: assignment.studentId,
          courseId: assignment.course_id,
          actualCourseId: assignment.course_id,
          hasResponses: isAssessment && hasLaunched && !shouldHideResponses,
          type: 'student',
          open: true,
          isgUrl,
        },
      });
      return;
    }

    if (shouldCloseSidePanel && sidePanelState.open) {
      sidePanelDispatch({ type: CLOSE_SIDE_PANEL });
    }

    if (assignment && !isUngraded) {
      dispatch(
        updateGradebook({
          showGradeOverrideDialog: true,
          gradeOverrideData: {
            ...assignment,
            hasStudentReport,
            dueDate,
            userId: assignment.studentId,
            assignmentId: assignment.id,
            userName: assignment.studentName || studentFullName,
            assignedPoints: getAssignedPoints(assignment),
            studentAssignmentLink:
              !assignment.isUngraded && studentAssignmentLink,
            studentReportLink: getStudentReportLink(
              assignment.studentId,
              assignment.assignmentId,
            ),
          },
        }),
      );
    }
  };

  if (isMobile && isTotal) {
    if (categoryName.toLowerCase() === 'ungraded') {
      return (
        <div className={s.mobileTotalScore}>
          <div className={s.mobileScorePoints}>{scoreData.content}</div>
        </div>
      );
    }

    const percent = getStudentRowData({
      cellData: assignment,
      showPercent: true,
      roundPercentType,
      isIndividualStudentGradebook: true,
      user,
    });

    return (
      <div className={s.mobileTotalScore}>
        <div className={s.mobileScorePoints}>{scoreData.content}</div>
        <div>
          <div className={s.mobileScoreOutOf}>
            Out of {roundToTwoDecimals(assignment.assignedPoints)}
          </div>
          <div className={s.mobileScorePercent}>{percent.content}</div>
        </div>
      </div>
    );
  }

  return (
    <div
      role="cell"
      data-test-id="isg-score-cell"
      className={classNames(s.scoreCellContainer, {
        [s.scoreCellClickable]: !isUngraded && !isAggregateCell,
        [s.hoverCell]: shouldSidePanelBeActive && !isAggregateCell,
        [s.cellContainerSelected]: isCellSelected,
      })}
    >
      {receivedLatePenalty && !isOverdue && (
        <span className={s.latePenaltyLabel}>Late</span>
      )}
      {isCellSelected && <GridCellBorder />}
      <div className={s.scoreContainer}>
        <span
          onClick={() => !isAggregateCell && showGradeOverride()}
          role={isAggregateCell ? undefined : 'button'}
          aria-label={
            isAggregateCell
              ? undefined
              : `View or override grade for ${assignment.name || 'this assignment'}`
          }
        >
          {scoreData.content}
        </span>
        {isOverdue && <span className={s.overdueLabel}>Overdue</span>}
      </div>
    </div>
  );
};

export const DateCell = ({ date, hide, hasException, mobileView }) => {
  const dateParsed = date && dayjs(date);
  const dateString =
    dateParsed && dateParsed.isValid() && dateParsed.format('MMM D, YYYY');
  const timeString =
    dateParsed && dateParsed.isValid() && dateParsed.format('h:mm a');

  const renderDueDateExceptionIcon = () => {
    return (
      <MLTooltip content="Due date exception provided" placement="top">
        <MLIcon type="info_outline" className={s.infoIcon} />
      </MLTooltip>
    );
  };

  if (mobileView === MOBILE_VIEW.DUE_DATE) {
    return (
      <div className={s.mobileDueDateLabel}>
        <span>Due Date: </span>
        <span className={s.dueDateLabel}>{dateString}</span>
        {hasException && renderDueDateExceptionIcon()}
      </div>
    );
  }

  if (mobileView === MOBILE_VIEW.COMPLETED) {
    return (
      <div className={s.dateLabel}>
        <span>Completed: </span>
        <span className={s.bold}>{dateString}</span>
        <span className={s.bold}>{timeString}</span>
      </div>
    );
  }

  if (dateString && timeString) {
    return (
      <div role="cell">
        {hasException && renderDueDateExceptionIcon()}
        <div>
          <div>{dateString}</div>
          <div>{timeString}</div>
        </div>
      </div>
    );
  }

  if (hide) return <div role="cell" />;

  return <div role="cell">--</div>;
};

export const DueDateCell = ({ assignment, isMobile }) => {
  const dueDateException = getDueDateException(assignment);
  const studentExceptionDate = getStudentAssignmentExceptionDate(assignment);

  const dueDate =
    dueDateException || studentExceptionDate || assignment.assignment_due_at;
  const isDueDateException =
    dueDateException && !isEqual(dueDate, assignment.assignment_due_at);

  return (
    <DateCell
      date={dueDate}
      hide={assignment.isAggregateCell}
      hasException={isDueDateException}
      mobileView={isMobile ? MOBILE_VIEW.DUE_DATE : null}
    />
  );
};

export const TimeSpentCell = ({ assignment, isMobile }) => {
  const formattedTimeSpent = getFormattedTime(assignment.timeOnTaskInMins);
  const timeException = hasTimeAccommodationException(assignment);

  const renderTimeException = () => {
    return (
      <MLTooltip content="Time limit accommodation provided" placement="top">
        <MLIcon type="info_outline" className={s.infoIcon} />
      </MLTooltip>
    );
  };

  if (isMobile) {
    return (
      <div className={s.mobileTimeSpentLabel}>
        <span>Time Spent: </span>
        <span className={s.timeSpentLabel}>{formattedTimeSpent}</span>
        {timeException && renderTimeException()}
      </div>
    );
  }

  return (
    <div role="cell">
      {timeException && renderTimeException()}
      {formattedTimeSpent}
    </div>
  );
};

export const MobileScoreCell = ({ assignment = {}, settings, onClick }) => {
  const {
    isUngraded,
    tool,
    status,
    assigned_points: assignedPoints,
    isAssigned,
    studentGrade,
    isAggregateCell,
    receivedLatePenalty,
    weightedCategoryTotal = 0,
  } = assignment;

  if (isUngraded && !isAggregateCell) {
    return <UngradedIcon status={status} tool={tool} />;
  }

  const score = formatDisplayPoints(assignment, settings);
  const gradingType = getGradingType(settings);
  const roundPercentType = getRoundPercentTypeValue(settings);
  const isWeighted = gradingType === GRADEBOOK_SETTINGS.gradingTypeWeighted;

  const percent =
    isWeighted && isAggregateCell
      ? formatDisplayPercentage(weightedCategoryTotal, roundPercentType)
      : formatDisplayGrade(
          studentGrade,
          assignment,
          settings,
          isAssigned,
          roundPercentType,
        );

  return (
    <div className={s.scoreInfo} onClick={onClick} role="button">
      <span className={s.mobileScorePoints}>{score}</span>
      {isFinite(assignedPoints) && (
        <div className={s.mobileScoreOutOf}>
          Out of {round(assignedPoints, 0)}
        </div>
      )}
      {percent && <div className={s.mobileScorePercent}>{percent}</div>}
      {receivedLatePenalty && <div className={s.latePenaltyLabel}>Late</div>}
    </div>
  );
};

const TimeSpentInfo = ({ onClose }) => {
  return createPortal(
    <div className={s.backdrop}>
      <div className={s.modal}>
        <MLIcon
          className={s.closeIcon}
          type="x"
          onClick={onClose}
          role="button"
        />
        <div>
          <h3 className={s.modalHeader}>How was this insight calculated?</h3>
          <p>
            The total time for an assignment is calculated by adding up all the
            time the student spends interacting with that assignment from start
            to finish. We "stop the clock" any time the student doesn't perform
            any action on the activity for about 10 minutes.
          </p>
          <p>
            Note that activity averages (scores and time) only included students
            that completed the activity. If a student did not complete the
            activity, their data is not included in this chart. Therefore, there
            may be some differences in the averages shown here and what you see
            in the Gradebook.
          </p>
          <button className={s.closeButton} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.getElementsByTagName('body')[0],
  );
};

const TooltipContent = ({ handleTooltipClose }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      {isOpen && (
        <TimeSpentInfo
          onClose={() => {
            handleTooltipClose();
            setIsOpen(false);
          }}
        />
      )}
      <div onClick={(e) => e.stopPropagation()} role="button">
        <div>
          Time metrics are only available for assessment or adaptive quiz
          activities. The average time per student is updated every 4 hours and
          may not be up to date.
        </div>
        <button onClick={() => setIsOpen(true)}>Learn more</button>
      </div>
    </>
  );
};

const TimeExceptionTooltip = () => {
  const [open, setOpen] = useState(false);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = (e) => {
    e.stopPropagation();
    setOpen(!open);
  };

  return (
    <div>
      <ClickAwayListener onClickAway={handleTooltipClose}>
        <div>
          <Tooltip
            classes={{
              tooltip: s.timeSpentTooltip,
              arrow: s.timeSpentArrow,
            }}
            title={<TooltipContent handleTooltipClose={handleTooltipClose} />}
            placement="top-end"
            onClose={handleTooltipClose}
            open={open}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            interactive
            arrow
          >
            <MLIcon
              type="info_outline"
              className={s.timeSpentIcon}
              onClick={handleTooltipOpen}
            />
          </Tooltip>
        </div>
      </ClickAwayListener>
    </div>
  );
};

export const HeaderCell = ({ label, sortOrder, onClick, sortBy }) => {
  const isTimeException = label === 'Time Spent';
  const isCurrentSortColumn = sortBy === camelCase(label);

  const getAriaSortValue = () => {
    if (!isCurrentSortColumn) return 'none';
    return sortOrder === ISG_SORT_ORDER_OPTIONS.ASC
      ? 'ascending'
      : 'descending';
  };

  const getAriaLabel = () => {
    if (!isCurrentSortColumn) {
      return `Sort by ${label}`;
    }
    const direction =
      sortOrder === ISG_SORT_ORDER_OPTIONS.ASC ? 'ascending' : 'descending';
    return `Sort by ${label}, currently sorted ${direction}. Click to reverse sort order.`;
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick(label);
    }
  };

  return (
    <div
      key={label}
      className={s.headerCell}
      onClick={() => onClick(label)}
      onKeyDown={handleKeyDown}
      data-test-id={`individual-student-gb-sort-${kebabCase(label)}`}
      role="columnheader"
      tabIndex={0}
      aria-label={getAriaLabel()}
      aria-sort={getAriaSortValue()}
    >
      {isTimeException && <TimeExceptionTooltip />}
      <div>{label}</div>
      {isCurrentSortColumn && (
        <MLIcon
          type="caret_down"
          role="presentation"
          className={s.caretIcon}
          style={{
            transform:
              sortOrder === ISG_SORT_ORDER_OPTIONS.ASC ? 'rotate(180deg)' : '',
          }}
        />
      )}
    </div>
  );
};
